// theming/getThemeCardVisuals.js
import { readableColor } from 'polished';

const normFamily = f => (f && f !== 'default' ? f : null);

function isDarkSurface(color) {
  try {
    return readableColor(color, '#ffffff', '#000000') === '#ffffff';
  } catch {
    return false;
  }
}

function fontLabel(tokens = {}) {
  const main = normFamily(tokens.fontMain?.family);
  const heading = normFamily(tokens.fontHeading?.family) || main;
  const body = normFamily(tokens.fontBody?.family) || main;
  const uniq = [...new Set([heading, body].filter(Boolean))];
  if (uniq.length === 0) return 'Inter';
  return uniq.join(' · ');
}

/**
 * Derives the at-a-glance visual model for a theme card from its tokens.
 * Pure — does not load fonts (the card does that with useFontLoader).
 */
export function getThemeCardVisuals(theme, currentMode) {
  const tokens = theme?.variables?.tokens || {};
  const colors = tokens.colors || 'light';
  const fallback = currentMode === 'dark' ? 'dark' : 'light';
  const mode = colors === 'dark' ? 'dark' : colors === 'light' ? 'light' : fallback;

  const colorAt = (c, fallback) => (c && c[mode]) || fallback;

  const surfaceFallback = mode === 'dark' ? '#0a0a0a' : '#ffffff';
  const accentFallback = mode === 'dark' ? '#ffffff' : '#000000';
  const textFallback = mode === 'dark' ? '#ffffff' : '#0a0a0a';

  const surface = colorAt(tokens.colorSurface, surfaceFallback);
  const accent = colorAt(tokens.colorPrimary, colorAt(tokens.colorSecondary, accentFallback));
  const text = colorAt(tokens.textPrimary, colorAt(tokens.textSecondary, textFallback));
  const textMuted = colorAt(tokens.textSecondary, text);
  const hoverOverlay = isDarkSurface(surface) ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.03)';

  const name = (theme?.name || '').trim();

  return {
    initial: name ? name.charAt(0).toUpperCase() : '?',
    fontFamily:
      normFamily(tokens.fontHeading?.family) || normFamily(tokens.fontMain?.family) || null,
    fontWeight: tokens.fontHeading?.weight || tokens.fontMain?.weight || 600,
    fontLabel: fontLabel(tokens),
    surface,
    accent,
    text,
    textMuted,
    hoverOverlay,
    letterColor: text,
    supportsLight: colors !== 'dark',
    supportsDark: colors !== 'light',
  };
}

export default getThemeCardVisuals;
